import { createSelector } from "reselect";
import { addLeadingSlashIfMissing, getKernelIdPath } from ".";
import BatchedRunStatus from "../../types/BatchedRunStatus";
import { AppState } from "../types";

const getCurrentBatchedRun = (state: AppState) =>
  state.batchedRun ? state.batchedRun : null;

/** Selects the current batch Kernel run id. */
export const getBatchedKernelRunId = createSelector(
  [getCurrentBatchedRun],
  (run): number | null => (run ? run.id : null)
);

/** Selects the current batch Kernel run status. */
export const getBatchedKernelRunStatus = createSelector(
  [getCurrentBatchedRun],
  run => (run ? run.status : null)
);

export const getShouldShowCommitProgress = createSelector(
  [getCurrentBatchedRun],
  run => run?.showProgressSnackbar
);

export const getBatchedRunUploadProgress = createSelector(
  [getCurrentBatchedRun],
  run => (run ? run.uploadProgress : null)
);

export const getBatchedRunVersionType = createSelector(
  [getCurrentBatchedRun],
  run => (run ? run.versionType : null)
);

export const getBatchedRunVersionUrl = createSelector(
  [getCurrentBatchedRun, getKernelIdPath],
  (run, kernelIdPath) => {
    if (run && run.id && kernelIdPath) {
      kernelIdPath = addLeadingSlashIfMissing(kernelIdPath);

      return `${window.location.origin}${kernelIdPath}?scriptVersionId=${run.id}`;
    }

    return null;
  }
);

/** Selects the text for the progress snackbar */

export const getBatchedRunText = (runId: number | null, state: AppState) => {
  const status = getBatchedKernelRunStatus(state);
  const uploadProgress = getBatchedRunUploadProgress(state);
  const lastRunLogLine = getLastRunLogLine(runId, state);
  switch (status) {
    case BatchedRunStatus.COMMITTING:
      return "Committing...";
    case BatchedRunStatus.UPLOADING:
      return `Uploading notebook (${uploadProgress})...`;
    case BatchedRunStatus.RUNNING:
      return lastRunLogLine ? lastRunLogLine : "Processing...";
    case BatchedRunStatus.CANCELLING:
      return "Cancelling...";
    case BatchedRunStatus.CANCELLED:
      return "Cancelled.";
    case BatchedRunStatus.COMPLETE:
      return "Done!";
    case BatchedRunStatus.ERROR:
      return "Failed";
    case BatchedRunStatus.UPLOADING_FILES:
      return "Uploading output files...";
    default:
      return "";
  }
};

export const getBatchSessionDateCreated = (
  sessionId: number | null,
  state: AppState
) => {
  if (!sessionId || !state.entities.runs[sessionId]) {
    return null;
  }

  return state.entities.runs[sessionId].dateCreated;
};

/** Gets run logs for the current version modal. */
export const getBatchedRunLogs = (runId: number | null) => (
  state: AppState
) => {
  const runs = state.entities.runs;
  const run = runId ? runs[runId] || null : null;

  if (run && state.batchedRun && state.batchedRun.id === runId) {
    const stdOutLines = run.stdOut.map(el => ({
      elapsedSeconds: el.key,
      content: el.value,
      streamName: "stdout",
    }));

    const stdErrLines = run.stdErr.map(el => ({
      elapsedSeconds: el.key,
      content: el.value,
      streamName: "stderr",
    }));

    const combined = stdErrLines.concat(stdOutLines);
    combined.sort((a, b) => a.elapsedSeconds - b.elapsedSeconds);
    return combined.map((streamItem, lineNumber) => ({
      lineNumber,
      ...streamItem,
    }));
  } else {
    return run ? run.logs : null;
  }
};

export const getLastRunLogLine = (runId: number | null, state: AppState) => {
  const logs = getBatchedRunLogs(runId)(state);
  if (logs && logs.length) {
    const logLine = logs[logs.length - 1];
    return logLine ? logLine.content : "";
  } else {
    return "";
  }
};
